import React, { useState, useCallback, useMemo, useContext, useEffect, useRef } from "react";
import { PanelMenu } from "primereact/panelmenu";
import { Toast } from "primereact/toast";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getItems } from "./menu";
import { CustomThemeContext } from "../../ThemeProvider/CustomThemeProvider";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { APP_URLS } from "../../../utils/constant";
import { removeWordAfterAt } from "../../../utils";
import { getAllFavorites, addFavorite, removeFavorite } from "../../../services/business/Favorites";
import "./style.css";

const newMenuKeys = [
  "reservation-detail-report",
  "daily-manifest-report",
  "bookings-by-location",
  "prepaid-bookings",
];

// Explicit mapping for API titles that don't match menu labels directly
// Format: "API menuTitle" -> "Menu label"
const apiToMenuLabelMap = {
  "Renter Log Interactions": "Renter Interaction Log",
};

// Reverse mapping: Menu label -> API title (for toggleFavorite)
// Used when menu label differs from API title
const menuLabelToApiTitleMap = {
  "Renter Interaction Log": "Renter Log Interactions",
};

const SideNav = ({ onMenuItemClick, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  const { currentTheme, setTheme } = useContext(CustomThemeContext);
  const isDarkMode = currentTheme === 'dark';

  const handleThemeToggle = () => {
    setTheme(currentTheme === 'light' ? 'dark' : 'light');
  };

  const handleLogout = () => {
    const appTheme = localStorage.getItem("appTheme");
    localStorage.clear();
    if (appTheme) {
      localStorage.setItem("appTheme", appTheme);
    }
    window.location.replace("/");
  };
  const [expandedKeys, setExpandedKeys] = useState({});
  const access = JSON.parse(localStorage.getItem("menuAccess")) || [];
  const [favorites, setFavorites] = useState([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);
  const items = useMemo(() => getItems(access), [access]);
  const rootKeys = useMemo(() => items.map((item) => item.key), [items]);

  // Helper function to normalize text for comparison
  // Handles camelCase like "AcType" → "ac type" and removes special chars
  const normalizeForComparison = useCallback((text) => {
    if (!text) return "";
    return text
      // Insert space before uppercase letters (for camelCase like AcType → Ac Type)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase()
      .replace(/[\/&-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  // Helper function to find menu item by label (returns key, path, and label)
  const findMenuItemByLabel = useCallback((menuItems, targetLabel) => {
    const normalizedTarget = normalizeForComparison(targetLabel);
    const compactTarget = normalizedTarget.replace(/\s+/g, "");
    // Remove common suffixes/prefixes for flexible matching
    const targetWithoutNew = normalizedTarget.replace(/\s*new\s*$/i, "").trim();
    const targetWithoutVehicle = normalizedTarget.replace(/^vehicle\s*/i, "").trim();
    // Also try converting target to key format (e.g., "Vehicle AcType" -> "vehicle-ac-type")
    const targetAsKey = normalizedTarget.replace(/\s+/g, "-");
    // Words for word-by-word matching
    const targetWords = targetWithoutNew.split(" ");
    
    // Helper for word-by-word matching with singular/plural tolerance
    const wordsMatchWithPlural = (words1, words2) => {
      if (words1.length !== words2.length) return false;
      return words1.every((word1, i) => {
        const word2 = words2[i];
        return word1 === word2 || 
               word1 === word2 + "s" || 
               word1 + "s" === word2;
      });
    };
    
    for (const item of menuItems) {
      const normalizedLabel = normalizeForComparison(item.label);
      const compactLabel = normalizedLabel.replace(/\s+/g, "");
      const normalizedKey = item.key?.toLowerCase() || "";
      // Remove "vehicle" prefix from menu label for comparison
      const labelWithoutVehicle = normalizedLabel.replace(/^vehicle\s*/i, "").trim();
      // Remove "new" suffix from menu label
      const labelWithoutNew = normalizedLabel.replace(/\s*new\s*$/i, "").trim();
      // Words for word-by-word matching
      const labelWords = labelWithoutNew.split(" ");
      
      // Check various matching strategies:
      // 1. Exact label match
      // 2. Compact match (no spaces)
      // 3. Target converted to key format matches item key
      // 4. Menu label without "Vehicle" prefix matches target
      // 5. Target without "New" suffix matches menu label without "New"
      // 6. Target without "Vehicle" matches label without "Vehicle"
      // 7. Singular/plural variations
      // 8. Word-by-word matching with singular/plural tolerance
      const isMatch = normalizedLabel === normalizedTarget ||
        compactLabel === compactTarget ||
        normalizedKey === targetAsKey ||
        normalizedKey === compactTarget.replace(/\s+/g, "-") ||
        // Match "AC Type" to "Vehicle AcType" (label without vehicle = target)
        labelWithoutVehicle === normalizedTarget ||
        labelWithoutVehicle === compactTarget ||
        labelWithoutVehicle.replace(/\s+/g, "") === compactTarget ||
        // Match "Prepaid Bookings Report New" to "Prepaid Bookings Report"
        labelWithoutNew === targetWithoutNew ||
        normalizedLabel === targetWithoutNew ||
        // Match target without vehicle to label without vehicle
        labelWithoutVehicle === targetWithoutVehicle ||
        // Singular/plural at end
        normalizedLabel === normalizedTarget + "s" ||
        normalizedLabel + "s" === normalizedTarget ||
        compactLabel === compactTarget + "s" ||
        compactLabel + "s" === compactTarget ||
        // Word-by-word matching (handles "Reservations Detail Report New" vs "Reservation Detail Report")
        wordsMatchWithPlural(labelWords, targetWords) ||
        wordsMatchWithPlural(targetWords, labelWords);
      
      if (isMatch && item.path && item.path !== "#") {
        return { key: item.key, path: item.path, label: item.label };
      }
      
      if (item.items) {
        const found = findMenuItemByLabel(item.items, targetLabel);
        if (found) return found;
      }
    }
    return null;
  }, [normalizeForComparison]);

  // Helper function to find path from menu items by label (for backward compatibility)
  const findPathByLabel = useCallback((menuItems, targetLabel) => {
    const result = findMenuItemByLabel(menuItems, targetLabel);
    return result?.path || null;
  }, [findMenuItemByLabel]);

  // Helper function to find menu item by key
  const findMenuItemByKey = useCallback((menuItems, targetKey) => {
    if (!targetKey) return null;
    const normalizedTargetKey = targetKey.toLowerCase().replace(/\s+/g, "-");
    
    for (const item of menuItems) {
      if (item.key === targetKey || item.key === normalizedTargetKey) {
        if (item.path && item.path !== "#") {
          return { key: item.key, path: item.path, label: item.label };
        }
      }
      
      if (item.items) {
        const found = findMenuItemByKey(item.items, targetKey);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Fetch favorites from API on mount
  const hasFetchedFavorites = useRef(false);
  useEffect(() => {
    if (hasFetchedFavorites.current) return;
    hasFetchedFavorites.current = true;

    const fetchFavorites = async () => {
      try {
        const response = await getAllFavorites();
        if (response?.status === "SUCCESS" && Array.isArray(response.favourites)) {
          // Map API response to local format
          const mappedFavorites = response.favourites
            .map((fav) => {
              // First, check explicit mapping for known mismatches
              const mappedLabel = apiToMenuLabelMap[fav.menuTitle];
              let menuItem = mappedLabel ? findMenuItemByLabel(items, mappedLabel) : null;
              
              // Try to find by menuCode (most reliable - should match menu key)
              if (!menuItem) {
                const menuCodeKey = fav.menuCode?.toLowerCase();
                menuItem = menuCodeKey ? findMenuItemByKey(items, menuCodeKey) : null;
              }
              
              // If not found by menuCode, try to find by label
              if (!menuItem) {
                menuItem = findMenuItemByLabel(items, fav.menuTitle);
              }
              
              // If still not found, try converting menuTitle to a key format
              if (!menuItem) {
                const titleAsKey = fav.menuTitle?.toLowerCase().replace(/\s+/g, "-");
                menuItem = findMenuItemByKey(items, titleAsKey);
              }
              
              if (menuItem) {
                // Use the actual menu item key to ensure proper matching
                return {
                  key: menuItem.key,
                  label: fav.menuTitle,
                  path: menuItem.path,
                  menuId: fav.menuId,
                };
              }
              
              // Final fallback: generate key from menuCode or menuTitle
              const fallbackKey = fav.menuCode?.toLowerCase() || fav.menuTitle?.toLowerCase().replace(/\s+/g, "-");
              const fallbackPath = fav.url;
              
              console.warn("Could not find menu item for favorite:", fav.menuTitle, "menuCode:", fav.menuCode, "using fallback key:", fallbackKey);
              
              return {
                key: fallbackKey,
                label: fav.menuTitle,
                path: fallbackPath,
                menuId: fav.menuId,
              };
            })
            // Filter out favorites with no valid path (parent menus like "Location Manager")
            .filter((fav) => fav.path && fav.path !== "#" && fav.path !== "");
          setFavorites(mappedFavorites);
        }
      } catch (error) {
        console.error("Failed to fetch favorites:", error);
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load favorites",
          life: 3000,
        });
      } finally {
        setIsLoadingFavorites(false);
      }
    };

    fetchFavorites();
  }, [items, findMenuItemByLabel, findMenuItemByKey]);

  const handleExpandedKeysChange = useCallback(
    (newExpandedKeys) => {
      let updatedKeys = {};

      const newlyOpenedKeys = Object.keys(newExpandedKeys).filter(
        (key) => newExpandedKeys[key]
      );
      if (newlyOpenedKeys.length > 0) {
        const lastOpenedKey = newlyOpenedKeys[newlyOpenedKeys.length - 1];

        if (rootKeys.includes(lastOpenedKey)) {
          updatedKeys = { [lastOpenedKey]: true };
        } else {
          updatedKeys = newExpandedKeys;
        }
      }
      setExpandedKeys(updatedKeys);
    },
    [rootKeys]
  );

  const isAnyNewMenuActive = useMemo(() => {
    const allMenuItems = [];
    const collectItems = (items) => {
      items.forEach((item) => {
        if (item.items) {
          collectItems(item.items);
        } else {
          allMenuItems.push(item);
        }
      });
    };
    collectItems(items);
    return allMenuItems.some(
      (item) =>
        newMenuKeys.includes(item.key) && `/${item.path}` === location.pathname
    );
  }, [location.pathname]);

  const handleNavigate = useCallback(
    (path) => {
      navigate(`/${path}`);
      // Close sidebar on mobile after navigation
      if (onMenuItemClick) {
        onMenuItemClick();
      }
    },
    [navigate, onMenuItemClick]
  );

  const toggleFavorite = useCallback(async (key, label, path) => {
    const isFav = favorites.some((fav) => fav.key === key);
    
    // Get menuId from mapping stored during login
    const menuIdMapping = JSON.parse(localStorage.getItem("menuIdMapping")) || {};
    
    // Check if this label has a known API title mapping (using the explicit map)
    const apiTitle = menuLabelToApiTitleMap[label];
    
    // Normalize using the same function that handles camelCase
    const normalizedLabel = normalizeForComparison(label);
    const normalizedApiTitle = apiTitle ? normalizeForComparison(apiTitle) : null;
    // Normalize the key (convert dashes to spaces for comparison)
    const normalizedKey = key?.toLowerCase().replace(/-/g, " ").trim();
    // Compact versions (no spaces) for fuzzy matching
    const compactKey = normalizedKey.replace(/\s+/g, "");
    const compactLabel = normalizedLabel.replace(/\s+/g, "");
    const compactApiTitle = normalizedApiTitle ? normalizedApiTitle.replace(/\s+/g, "") : null;
    
    // Helper to check if two strings match with flexible rules
    const flexibleMatch = (mappingTitle, targetLabel, targetKey) => {
      const normalizedMapping = mappingTitle.toLowerCase().replace(/\s+/g, " ").trim();
      // Remove "new" suffix from mapping title for comparison
      const mappingWithoutNew = normalizedMapping.replace(/\s*new\s*$/i, "").trim();
      const compactMapping = normalizedMapping.replace(/\s+/g, "");
      const compactMappingWithoutNew = mappingWithoutNew.replace(/\s+/g, "");
      
      // Check explicit API title mapping first (most reliable)
      if (normalizedApiTitle && normalizedMapping === normalizedApiTitle) return true;
      if (compactApiTitle && compactMapping === compactApiTitle) return true;
      
      // Direct matches
      if (normalizedMapping === targetLabel || normalizedMapping === targetKey) return true;
      if (compactMapping === compactLabel || compactMapping === compactKey) return true;
      
      // Match without "new" suffix
      if (mappingWithoutNew === targetLabel || mappingWithoutNew === targetKey) return true;
      if (compactMappingWithoutNew === compactLabel || compactMappingWithoutNew === compactKey) return true;
      
      // Singular/plural variations at end of phrase
      if (mappingWithoutNew === targetLabel + "s" || mappingWithoutNew + "s" === targetLabel) return true;
      if (mappingWithoutNew === targetKey + "s" || mappingWithoutNew + "s" === targetKey) return true;
      if (compactMappingWithoutNew === compactLabel + "s" || compactMappingWithoutNew + "s" === compactLabel) return true;
      if (compactMappingWithoutNew === compactKey + "s" || compactMappingWithoutNew + "s" === compactKey) return true;
      
      // Word-by-word matching with singular/plural tolerance
      // Handles "Reservation Detail Report" vs "Reservations Detail Report"
      const mappingWords = mappingWithoutNew.split(" ");
      const targetLabelWords = targetLabel.split(" ");
      const targetKeyWords = targetKey.split(" ");
      
      const wordsMatchWithPlural = (words1, words2) => {
        if (words1.length !== words2.length) return false;
        return words1.every((word1, i) => {
          const word2 = words2[i];
          return word1 === word2 || 
                 word1 === word2 + "s" || 
                 word1 + "s" === word2;
        });
      };
      
      if (wordsMatchWithPlural(mappingWords, targetLabelWords)) return true;
      if (wordsMatchWithPlural(mappingWords, targetKeyWords)) return true;
      
      return false;
    };
    
    // Try to find a matching menu using flexible matching
    let menuInfo = Object.entries(menuIdMapping).find(
      ([titleKey]) => flexibleMatch(titleKey, normalizedLabel, normalizedKey)
    );
    
    // If still no match, try partial match but exclude parent menus (those with "manager" in the name)
    if (!menuInfo) {
      menuInfo = Object.entries(menuIdMapping).find(
        ([titleKey]) => {
          const normalizedMapping = titleKey.toLowerCase().replace(/\s+/g, " ").trim();
          // Skip parent menus that contain "manager" to avoid matching "location" to "location manager"
          if (normalizedMapping.includes("manager")) return false;
          const mappingWithoutNew = normalizedMapping.replace(/\s*new\s*$/i, "").trim();
          return mappingWithoutNew.includes(normalizedLabel) || normalizedLabel.includes(mappingWithoutNew) ||
                 mappingWithoutNew.includes(normalizedKey) || normalizedKey.includes(mappingWithoutNew);
        }
      );
    }
    
    const menuId = menuInfo?.[1]?.menuId;

    if (!menuId) {
      console.warn("Menu not found for label:", label, "key:", key, "normalized label:", normalizedLabel, "normalized key:", normalizedKey, "available keys:", Object.keys(menuIdMapping));
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Unable to update favorite - menu not found",
        life: 3000,
      });
      return;
    }

    // Optimistic update
    const previousFavorites = [...favorites];
    const updatedFavorites = isFav
      ? favorites.filter((fav) => fav.key !== key)
      : [...favorites, { key, label, path, menuId }];
    setFavorites(updatedFavorites);

    try {
      if (isFav) {
        const response = await removeFavorite(menuId);
        if (response?.status !== "SUCCESS") {
          throw new Error(response?.message || "Failed to remove favorite");
        }
      } else {
        const response = await addFavorite(menuId);
        if (response?.status !== "SUCCESS") {
          throw new Error(response?.message || "Failed to add favorite");
        }
      }
    } catch (error) {
      // Revert on failure
      setFavorites(previousFavorites);
      console.error("Failed to update favorite:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to update favorite",
        life: 3000,
      });
    }
  }, [favorites, normalizeForComparison]);

  const handleRemoveFavorite = useCallback(async (fav) => {
    let menuId = fav.menuId;
    
    if (!menuId) {
      // Try to find menuId from mapping
      const menuIdMapping = JSON.parse(localStorage.getItem("menuIdMapping")) || {};
      const normalizedLabel = normalizeForComparison(fav.label);
      const normalizedKey = fav.key?.toLowerCase().replace(/-/g, " ").trim();
      const compactKey = normalizedKey?.replace(/\s+/g, "") || "";
      const compactLabel = normalizedLabel?.replace(/\s+/g, "") || "";
      
      // Try matching by key first (most reliable)
      let menuInfo = Object.entries(menuIdMapping).find(
        ([titleKey]) => titleKey === normalizedKey
      );
      
      // Try exact match by label
      if (!menuInfo) {
        menuInfo = Object.entries(menuIdMapping).find(
          ([titleKey]) => titleKey === normalizedLabel
        );
      }
      
      // Try matching with compact versions (no spaces) - handles "actype" vs "ac type"
      if (!menuInfo) {
        menuInfo = Object.entries(menuIdMapping).find(
          ([titleKey]) => {
            const compactTitleKey = titleKey.replace(/\s+/g, "");
            return compactTitleKey === compactKey || compactTitleKey === compactLabel;
          }
        );
      }
      
      // Try singular/plural variation
      if (!menuInfo) {
        menuInfo = Object.entries(menuIdMapping).find(
          ([titleKey]) => titleKey === normalizedLabel + "s" || titleKey + "s" === normalizedLabel ||
                          titleKey === normalizedKey + "s" || titleKey + "s" === normalizedKey
        );
      }
      
      // Partial match excluding parent menus
      if (!menuInfo) {
        menuInfo = Object.entries(menuIdMapping).find(
          ([titleKey]) => {
            if (titleKey.includes("manager")) return false;
            return titleKey.includes(normalizedLabel) || normalizedLabel.includes(titleKey) ||
                   titleKey.includes(normalizedKey) || normalizedKey.includes(titleKey);
          }
        );
      }
      
      if (!menuInfo?.[1]?.menuId) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Unable to remove favorite - menu not found",
          life: 3000,
        });
        return;
      }
      fav.menuId = menuInfo[1].menuId;
    }

    // Optimistic update
    const previousFavorites = [...favorites];
    setFavorites(favorites.filter((f) => f.key !== fav.key));

    try {
      const response = await removeFavorite(fav.menuId);
      if (response?.status !== "SUCCESS") {
        throw new Error(response?.message || "Failed to remove favorite");
      }
    } catch (error) {
      // Revert on failure
      setFavorites(previousFavorites);
      console.error("Failed to remove favorite:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to remove favorite",
        life: 3000,
      });
    }
  }, [favorites, normalizeForComparison]);

  const renderMenuItems = useCallback(
    (menuItems) => {
      return menuItems.map((item) => {
        if (item.key === "favourites") {
          return {
            ...item,
            items: favorites.map((fav) => ({
              label: fav.label,
              key: fav.key,
              path: fav.path,
              template: (menuItem, options) => (
                <div
                  className={options.className}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigate(fav.path);
                  }}
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    color: isDarkMode ? '#f1f5f9' : 'inherit',
                    cursor: "pointer", 
                    WebkitTapHighlightColor: "transparent",
                    width: "100%",
                    gap: "4px",
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveFavorite(fav);
                    }}
                    style={{ 
                      cursor: "pointer", 
                      padding: "2px", 
                      WebkitTapHighlightColor: "transparent",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <i
                      className="pi pi-star-fill"
                      style={{ color: '#fbbf24', fontSize: "12px" }}
                    />
                  </span>
                  <span style={{ 
                    color: isDarkMode ? '#f1f5f9' : 'inherit',
                    flex: 1, 
                    fontSize: "12px",
                    lineHeight: "1.3",
                  }}>
                    {menuItem.label}
                  </span>
                </div>
              ),
              command: () => handleNavigate(fav.path),
            })),
          };
        }

        const isFavorite = favorites.some((fav) => fav.key === item.key);
        const isDashboard = item.key === "dashboard";

        // Dark mode colors for menu items
        const textColor = isDarkMode ? '#f1f5f9' : 'inherit';
        const iconColor = isDarkMode ? '#94a3b8' : 'inherit';
        const starColor = isFavorite ? '#fbbf24' : (isDarkMode ? '#94a3b8' : 'inherit');

        // Dashboard: render as a standard panel header (no custom template)
        if (isDashboard) {
          return {
            ...item,
            command: () => {
              handleNavigate(item.path);
              setExpandedKeys({});
            },
          };
        }

        // Analytics: opens external URL in new tab
        if (item.key === "analytics") {
          return {
            ...item,
            command: () => {
              window.open(item.externalUrl, "_blank", "noopener,noreferrer");
              if (onMenuItemClick) onMenuItemClick();
            },
          };
        }

        return {
          ...item,
          ...(item.items
            ? { items: renderMenuItems(item.items) }
            : {
                template: (menuItem, options) => (
                  <div
                    className={options.className}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigate(item.path);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNavigate(item.path);
                      }
                    }}
                    style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      color: textColor, 
                      cursor: "pointer", 
                      WebkitTapHighlightColor: "transparent",
                      width: "100%",
                      gap: "4px",
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleFavorite(item.key, item.label, item.path);
                      }}
                      style={{ 
                        cursor: "pointer", 
                        padding: "2px", 
                        WebkitTapHighlightColor: "transparent",
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <i
                        className={`pi ${
                          isFavorite ? "pi-star-fill" : "pi-star"
                        }`}
                        style={{ color: starColor, fontSize: "12px" }}
                      />
                    </span>
                    <span style={{ 
                      color: textColor, 
                      flex: 1, 
                      fontSize: "12px",
                      lineHeight: "1.3",
                    }}>
                      {menuItem.label}
                    </span>

                    {newMenuKeys.includes(item.key) && (
                      <span
                        className={`new-badge ${
                          isAnyNewMenuActive ? "active" : ""
                        }`}
                      >
                        New
                      </span>
                    )}
                  </div>
                ),
                command: () => {
                  handleNavigate(item.path);
                },
              }),
        };
      });
    },
    [favorites, handleNavigate, toggleFavorite, handleRemoveFavorite, isAnyNewMenuActive, isDarkMode]
  );

  const menuModel = useMemo(() => renderMenuItems(items), [renderMenuItems]);

  // Dark mode inline styles
  const containerStyle = isDarkMode ? {
    backgroundColor: '#0f172a',
    borderRight: '1px solid #1e293b'
  } : {};

  return (
    <div className="sidenav-container" style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toast ref={toastRef} />
      {/* Mobile Username Header */}
      {isMobile && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mr: 1, fontSize: 20 }} />
            <Typography sx={{ 
              color: isDarkMode ? '#f1f5f9' : '#334155', 
              fontSize: 14, 
              fontWeight: 600 
            }}>
              {removeWordAfterAt(localStorage.getItem("username"))}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Inject dark mode styles with high specificity */}
      {isDarkMode && (
        <style>{`
          .sidenav-container .p-panelmenu {
            background: transparent !important;
          }
          .sidenav-container .p-panelmenu-panel {
            background: transparent !important;
          }
          .sidenav-container .p-panelmenu-header {
            background: transparent !important;
            border: none !important;
          }
          .sidenav-container .p-panelmenu-header > .p-panelmenu-header-content {
            background: transparent !important;
            border: none !important;
          }
          .sidenav-container .p-panelmenu-header-action,
          .sidenav-container .p-panelmenu-header-link,
          .sidenav-container .p-panelmenu-header > a,
          .sidenav-container .p-panelmenu-header > .p-panelmenu-header-content > a,
          .sidenav-container .p-panelmenu-header > .p-panelmenu-header-content > .p-panelmenu-header-action {
            background: transparent !important;
            border: none !important;
            color: #f1f5f9 !important;
          }
          .sidenav-container .p-panelmenu-header-action:hover,
          .sidenav-container .p-panelmenu-header-link:hover,
          .sidenav-container .p-panelmenu-header > a:hover,
          .sidenav-container .p-panelmenu-header > .p-panelmenu-header-content > a:hover,
          .sidenav-container .p-panelmenu-header > .p-panelmenu-header-content > .p-panelmenu-header-action:hover {
            background: #334155 !important;
            color: #ffffff !important;
          }
          .sidenav-container .p-menuitem-text,
          .sidenav-container .p-panelmenu .p-menuitem-text {
            color: #f1f5f9 !important;
          }
          .sidenav-container .p-menuitem-icon,
          .sidenav-container .p-panelmenu .p-menuitem-icon,
          .sidenav-container .p-submenu-icon {
            color: #94a3b8 !important;
          }
          .sidenav-container .p-panelmenu-content {
            background: transparent !important;
            border: none !important;
          }
          .sidenav-container .p-menuitem-link,
          .sidenav-container .p-menuitem > .p-menuitem-content,
          .sidenav-container .p-menuitem > .p-menuitem-content > .p-menuitem-link {
            background: transparent !important;
            color: #e2e8f0 !important;
          }
          .sidenav-container .p-menuitem-link:hover,
          .sidenav-container .p-menuitem > .p-menuitem-content:hover,
          .sidenav-container .p-menuitem:not(.p-disabled) > .p-menuitem-content:hover {
            background: #334155 !important;
          }
          .sidenav-container .p-menuitem-link:hover .p-menuitem-text,
          .sidenav-container .p-menuitem:not(.p-disabled) > .p-menuitem-content:hover .p-menuitem-text {
            color: #ffffff !important;
          }
          .sidenav-container .p-highlight > .p-panelmenu-header-content,
          .sidenav-container .p-panelmenu-header.p-highlight > .p-panelmenu-header-content {
            background: rgba(91, 122, 184, 0.18) !important;
          }
          .sidenav-container .p-focus > .p-menuitem-content {
            background: rgba(91, 122, 184, 0.15) !important;
            box-shadow: none !important;
          }
        `}</style>
      )}
      
      {/* Menu Content - Scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <PanelMenu
          model={menuModel}
          className={`custom-panelmenu ${isDarkMode ? 'dark-mode' : ''}`}
          expandedKeys={expandedKeys}
          onExpandedKeysChange={handleExpandedKeysChange}
        />
      </Box>

      {/* Mobile Action Buttons - Below Menu */}
      {isMobile && (
        <Box sx={{ 
          px: 2, 
          py: 2, 
          borderTop: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
          backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Dark Mode Toggle */}
            <Box
              onClick={handleThemeToggle}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#475569' : '#e2e8f0',
                },
              }}
            >
              {currentTheme === 'light' ? (
                <DarkModeIcon sx={{ fontSize: 18, color: isDarkMode ? '#f1f5f9' : '#334155' }} />
              ) : (
                <LightModeIcon sx={{ fontSize: 18, color: isDarkMode ? '#f1f5f9' : '#334155' }} />
              )}
              <Typography sx={{ fontSize: 12, color: isDarkMode ? '#f1f5f9' : '#334155' }}>
                {currentTheme === 'light' ? 'Dark' : 'Light'}
              </Typography>
            </Box>

            {/* Profile Link */}
            <Link to={APP_URLS.CHANGEPASSWORD} style={{ textDecoration: 'none' }} onClick={onMenuItemClick}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#475569' : '#e2e8f0',
                  },
                }}
              >
                <PersonIcon sx={{ fontSize: 18, color: isDarkMode ? '#f1f5f9' : '#334155' }} />
                <Typography sx={{ fontSize: 12, color: isDarkMode ? '#f1f5f9' : '#334155' }}>
                  Profile
                </Typography>
              </Box>
            </Link>

            {/* Logout Button */}
            <Box
              onClick={handleLogout}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: isDarkMode ? '#7f1d1d' : '#fef2f2',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#991b1b' : '#fee2e2',
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18, color: isDarkMode ? '#fecaca' : '#dc2626' }} />
              <Typography sx={{ fontSize: 12, color: isDarkMode ? '#fecaca' : '#dc2626' }}>
                Logout
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default SideNav;
